import { Dispatch } from "redux"
import {GET_ORDER_BOOK, GET_ORDER_BOOKS, tickSize} from "../assets/constants"
import {futureTradeDataType, optionsTradesDataType, optionTradeDataType} from "../pages/typings/options.typings"
import {OiDataTypeBars} from "../pages/home/plChart"
import { Order, OrderStatus, TransactionType } from "../proto/generated/order"
import isEqual from "lodash/isEqual";
import {OrderBook, OrderBooks, Security, SecurityCategory_CategoryCode} from "../proto/generated/security"
import {OrderDataPrices, OrderState, OrdersDataType, setOrderPrices} from "../reducer/orders.reducer"
import api from "../services/apis/axiosClient"
import { PayoffDataType } from "../components/payoffChart/PayoffWrapper"
import { getSecurityAsync, getSecurityKey } from "./security"
import { limitPriceFromOrderBookOrLtp } from "./orderBookPrice"
import store from "../store"
import { updateOrderBooks } from "../reducer/security.reducer"
import { EqualityOperator } from "../components/strategyTemplate/StrategyTemplate.types"
import { calculateDifferenceInDays, getProfitsForSecurityAtDate, getSortedTradedExpiries } from "./strategyBuilder"
import { StrategyInfo, TradeOrder } from "../reducer/strategyBuilder.reducer"
import { calculateGcd } from "./orders"
import { isExpressionValid } from "./expression"
import { GroupedPosition } from "../reducer/positions.reducer"
import { Position } from "../proto/generated/position"
import { OptionGreeks } from "../proto/generated/common"
import { TradingCalendarData } from "../proto/generated/trading-calendar"

export function orderTypeToTransactionType(type: 'buy' | 'sell'): TransactionType {
    return type === 'buy' ? TransactionType.BUY : TransactionType.SELL
}

export function findNearestNumber(arr: number[], x: number): number {
    let left = 0
    let right = arr.length - 1
    let closest = arr[0]
    let minDiff = Math.abs(x - closest)
    while (left <= right) {
        const mid = Math.floor((left + right) / 2)
        const current = arr[mid]
        const currentDiff = Math.abs(x - current)
        if (currentDiff < minDiff) {
            minDiff = currentDiff
            closest = current
        }
        if (current === x) {
            return current
        } else if (current < x) {
            left = mid + 1
        } else {
            right = mid - 1
        }
    }
    return closest
}

export function findNearestNumberIndex(
    arr: { price: number; index: number }[],
    x: number,
    condition: EqualityOperator
  ) {
    // Sort the array by price
    arr.sort((a, b) => a.price - b.price);
  
    let left = 0;
    let right = arr.length - 1;
  
    let closestIndex: number | null = null;
  
    // Adjusted logic for condition matching
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const current = arr[mid];
  
      if (current.price === x && (condition === EqualityOperator["~"] || condition === EqualityOperator["<="] || condition === EqualityOperator[">="])) {
        return current.index; // Exact match
      }
  
      if (current.price < x) {
        if (condition === EqualityOperator["<="]) closestIndex = current.index; // Keep track of the latest valid index
        left = mid + 1;
      } else if (current.price > x) {
        if (condition === EqualityOperator[">="] && (closestIndex === null || current.price <= arr[closestIndex].price)) {
          closestIndex = current.index; // Update closestIndex for '>='
        }
        right = mid - 1;
      } else {
        left = mid + 1; // For approximate matching (~)
      }
    }
  
    // Return based on the '~' condition
    if (condition === EqualityOperator["~"]) {
      let minDiff = Infinity;
      for (const item of arr) {
        const diff = Math.abs(x - item.price);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = item.index;
        }
      }
    }
  
    return closestIndex??0;
  }

export const arePlDataEqual = (data1: OiDataTypeBars[], data2: OiDataTypeBars[]) => {
    const plData1 = data1.sort((a, b) => (a.strike ?? 0) - (b.strike ?? 0)).map((dt) => ({
        strike: dt.strike,
        profit: dt.profit,
        expectedProfit: dt.expectedProfit
    }))
    const plData2 = data2.sort((a, b) => (a.strike ?? 0) - (b.strike ?? 0)).map((dt) => ({
        strike: dt.strike,
        profit: dt.profit,
        expectedProfit: dt.expectedProfit
    }))

    return isEqual(plData1, plData2)
}

/** Call delta usable for POP — treats unset/default proto greeks as missing. */
const usableCallDelta = (trade: optionTradeDataType | null | undefined): number | null => {
    const greeks = trade?.callOption?.optionGreeks
    if (!greeks) return null
    const delta = greeks.delta
    if (delta == null || !Number.isFinite(delta)) return null
    // Default OptionGreeks instance is all zeros when IV/greeks were never computed.
    if (delta === 0 && (greeks.gamma ?? 0) === 0 && (greeks.vega ?? 0) === 0) return null
    return delta
}

/**
 * Sensibull-style call Δ at a chain strike (raw strike units):
 * exact → else nearest lower → else nearest higher. Null if none.
 */
const resolveCallDeltaAtChainStrike = (
    trades: optionTradeDataType[],
    strikeChain: number,
): number | null => {
    if (!trades.length) return null
    const exact = trades.find((t) => t.strikeCol.strike === strikeChain)
    const exactDelta = usableCallDelta(exact)
    if (exactDelta != null) return exactDelta

    let lower: optionTradeDataType | undefined
    let higher: optionTradeDataType | undefined
    for (const t of trades) {
        const k = t.strikeCol.strike
        if (k < strikeChain) {
            if (usableCallDelta(t) != null && (!lower || k > lower.strikeCol.strike)) lower = t
        } else if (k > strikeChain) {
            if (usableCallDelta(t) != null && (!higher || k < higher.strikeCol.strike)) higher = t
        }
    }
    const lowerDelta = usableCallDelta(lower)
    const higherDelta = usableCallDelta(higher)
    if (lowerDelta != null && higherDelta == null) return lowerDelta
    if (higherDelta != null && lowerDelta == null) return higherDelta
    if (lowerDelta != null && higherDelta != null && lower && higher) {
        const denom = higher.strikeCol.strike - lower.strikeCol.strike
        if (denom === 0) return lowerDelta
        return lowerDelta + ((strikeChain - lower.strikeCol.strike) * (higherDelta - lowerDelta)) / denom
    }
    return null
}

/**
 * Call Δ at a breakeven (raw chain units):
 * - Exact listed strike → that Δ (with neighbor fill if greek missing)
 * - Between two strikes → lerp (each side neighbor-filled)
 * - Outside listed range (only one side) → null (no edge extrapolation; POP "-")
 */
const resolveCallDeltaAtBreakeven = (
    trades: optionTradeDataType[],
    breakevenChain: number,
): number | null => {
    if (!trades.length) return null
    let lower: optionTradeDataType | undefined
    let higher: optionTradeDataType | undefined
    for (const t of trades) {
        const k = t.strikeCol.strike
        if (k <= breakevenChain && (!lower || k > lower.strikeCol.strike)) lower = t
        if (k >= breakevenChain && (!higher || k < higher.strikeCol.strike)) higher = t
    }
    // Exact listed strike
    if (lower && higher && lower.strikeCol.strike === higher.strikeCol.strike) {
        return resolveCallDeltaAtChainStrike(trades, lower.strikeCol.strike)
    }
    // Outside chain — do not use edge Δ as BE Δ (extrapolation distorts POP)
    if (!lower || !higher) return null

    const lowerDelta = resolveCallDeltaAtChainStrike(trades, lower.strikeCol.strike)
    const higherDelta = resolveCallDeltaAtChainStrike(trades, higher.strikeCol.strike)
    if (lowerDelta == null || higherDelta == null) return null

    const denom = higher.strikeCol.strike - lower.strikeCol.strike
    if (denom === 0) return lowerDelta
    return lowerDelta + ((breakevenChain - lower.strikeCol.strike) * (higherDelta - lowerDelta)) / denom
}

const clampPopPercent = (probability: number): number =>
    Math.round(Math.min(100, Math.max(0, probability * 100)))

export const CalculatePopFunction = (data: OiDataTypeBars[], optionsExpiries: optionsTradesDataType[], orders: OrdersDataType[], priceMultiplier: number): number => {
    if (!data.length) return 0
    let probability = data[0].profit >= 0 ? 1 : 0
    const nearestExpiry = findNearestExpiry(orders, optionsExpiries)
    for (let i = 1; i < data.length; i++) {
        if (data[i].profit >= 0 && data[i - 1].profit < 0) {
            const deltaFound = CalculateDelta(data, i, optionsExpiries, nearestExpiry, priceMultiplier)
            if (deltaFound == null) return 0 // UI shows "-"
            probability += deltaFound
        } else if (data[i].profit < 0 && data[i - 1].profit >= 0) {
            const deltaFound = CalculateDelta(data, i, optionsExpiries, nearestExpiry, priceMultiplier)
            if (deltaFound == null) return 0 // UI shows "-"
            probability -= deltaFound
        }
    }
    return clampPopPercent(probability)
}

export const getOnlyOrderBook = async (args: { security: Partial<Security | undefined> }): Promise<OrderBook> => {
    return new Promise((resolve, reject) => {
        api.post(GET_ORDER_BOOK, JSON.stringify({
            code: args.security?.code,
            exchange: args.security?.exchange
        }), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const result = OrderBook.decode(new Uint8Array(response.data))
                resolve(result)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getOrderBooksBulk = async (securities: Partial<Security>[]): Promise<OrderBook[]> => {
    return new Promise((resolve, reject) => {
        api.post(GET_ORDER_BOOKS, JSON.stringify({
            securities: securities
        }), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const result = OrderBooks.decode(new Uint8Array(response.data))

                resolve(result.orderBooks)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export function isDatePassed(dateString: number) {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const currentDate = now.getTime()
    return dateString < currentDate
}

export function roundToNearestMultiple(number: number, type: TransactionType, floorOnly=false, TickSize = tickSize): number {
    const multiple = Number(Math.round(TickSize * 100))
    if (Number(Math.round(number * 100)) % Number(Math.round(TickSize * 100)) === 0) {
        return number
    }
    if (type === TransactionType.BUY && !floorOnly) {
        return Number(((Math.ceil(Number(Math.round(number * 100)) / multiple) * multiple) / 100).toFixed(2))
    }
    return Number(((Math.floor(Number(Math.round(number * 100)) / multiple) * multiple) / 100).toFixed(2))
}

export function ifFiniteNumber(num: string) {
    return !(num === '∞' || num === '-∞')
}

function normalCDF(X: number) {   //HASTINGS.  MAX ERROR = .000001
    let T = 1 / (1 + .2316419 * Math.abs(X))
    let D = .3989423 * Math.exp(-X * X / 2)
    let Prob = D * T * (.3193815 + T * (-.3565638 + T * (1.781478 + T * (-1.821256 + T * 1.330274))))
    if (X > 0) {
        Prob = 1 - Prob
    }
    return Prob
}

export function CalculateOptionPrice(s: number, k: number, r: number, t: number, v: number, is_call: boolean) {
    let dPlus = (Math.log(s / k) + (r + v * v * 0.5) * t) / (v * Math.sqrt(t))
    let dMinus = dPlus - v * Math.sqrt(t)
    return is_call ? (s * normalCDF(dPlus) - k * normalCDF(dMinus) * Math.exp(-1.0 * r * t)) :
        (k * normalCDF(-dMinus) * Math.exp(-1.0 * r * t) - normalCDF(-dPlus) * s)
}

const findNearestExpiry = (orders: OrdersDataType[], optionExpiries: optionsTradesDataType[]) => {
    let indexs: number[] = []
    orders.forEach((o) => {
        const ind = optionExpiries.findIndex(oe=>oe.expiration.expiry?.expiry===o.expiry)
        indexs.push(ind)
    })
    let i = 0
    let maxLength = optionExpiries.length - 1
    indexs.forEach((ind, j) => {
        if (ind < maxLength) {
            maxLength = ind
            i = j
        }
    })
    return optionExpiries.findIndex(o => o.expiration.expiry?.expiry === orders?.[i]?.expiry)
}

const CalculateDelta = (data: OiDataTypeBars[], i: number, optionExpiries: optionsTradesDataType[], nearestExpiry: number, priceMultiplier: number): number | undefined => {
    const trades = optionExpiries?.[nearestExpiry]?.trades
    if (!trades?.length) return undefined
    const strikeChain = Math.round((data[i].strike ?? 0) * priceMultiplier)
    const delta = resolveCallDeltaAtBreakeven(trades, strikeChain)
    return delta == null ? undefined : delta
}

export const generatePlChartData = ({data, priceMultiplier = 100, ltp: lastTradedPrice, filterLowValueStrikes = false}: {data: optionTradeDataType[], priceMultiplier?: number, ltp: number, filterLowValueStrikes?: boolean}) => {
    const ltp = lastTradedPrice / priceMultiplier    
    if (!Number.isFinite(ltp)) {
        return []
    }

    let maxOi = -Infinity
    const array = data
        ?.map((t, i) => {
            maxOi = Math.max(maxOi, t.callOption?.openInterest ?? 0, t.putOption?.openInterest ?? 0)
            if (i === data.length - 1) {
                const interval = (t.strikeCol.strike / priceMultiplier - data[i - 1].strikeCol.strike / priceMultiplier) / 5
                return [
                    {
                        strike: t.strikeCol.strike / priceMultiplier,
                        callOi: t.callOption?.openInterest,
                        putOi: t.putOption?.openInterest,
                        callDelta: t.callOption?.optionGreeks?.delta ?? 0,
                        putDelta: t.putOption?.optionGreeks?.delta ?? 0,
                        profit: 0,
                        expectedProfit: 0,
                        duplicateStrike: false,
                        axisStrike: true
                    },
                    {
                        strike: t.strikeCol.strike / priceMultiplier + interval,
                        callOi: 0,
                        putOi: 0,
                        callDelta: 0,
                        putDelta: 0,
                        profit: 0,
                        expectedProfit: 0,
                        duplicateStrike: true,
                        axisStrike: false
                    }
                ]
            }
            const interval = (data[i + 1].strikeCol.strike - t.strikeCol.strike) / priceMultiplier
            const returndata: OiDataTypeBars[] = [
                {
                    strike: t.strikeCol.strike / priceMultiplier,
                    callOi: t.callOption?.openInterest,
                    putOi: t.putOption?.openInterest,
                    callDelta: t.callOption?.optionGreeks?.delta ?? 0,
                    putDelta: t.putOption?.optionGreeks?.delta ?? 0,
                    profit: 0,
                    expectedProfit: 0,
                    duplicateStrike: false,
                    axisStrike: true
                }
            ]
            if (i === 0) {
                returndata.unshift({
                    strike: t.strikeCol.strike / priceMultiplier - interval,
                    callOi: 0,
                    putOi: 0,
                    callDelta: 0,
                    putDelta: 0,
                    profit: 0,
                    expectedProfit: 0,
                    duplicateStrike: true,
                    axisStrike: false
                })
            }

            for (let c = 1; c < interval; c++) {
                const currentStrike = t.strikeCol.strike / priceMultiplier + c
                returndata.push({
                    strike: currentStrike,
                    callOi: 0,
                    putOi: 0,
                    callDelta: 0,
                    putDelta: 0,
                    profit: 0,
                    expectedProfit: 0,
                    duplicateStrike: true,
                    axisStrike: false
                })
            }
            return returndata
        })
        .flat()
    if (!array) {
        return []
    }
    if(!filterLowValueStrikes) return array

    const thresholdOi = 0.2 * maxOi
    const strikesArray = array?.map((a) => a.strike ?? 0)
    const originalStrikesArray: OiDataTypeBars[] = array!.filter((a) => !a.duplicateStrike)
    let lastStrikeToBeConsidered: number = originalStrikesArray.length - 1
    let firstStrikeToBeConsidered: number = 0
    while(lastStrikeToBeConsidered >=0 && (!Number.isFinite(originalStrikesArray[lastStrikeToBeConsidered].callOi) || Number(originalStrikesArray[lastStrikeToBeConsidered].callOi) < thresholdOi) && (!Number.isFinite(originalStrikesArray[lastStrikeToBeConsidered].putOi) ||  Number(originalStrikesArray[lastStrikeToBeConsidered].putOi) < thresholdOi)){
        lastStrikeToBeConsidered--;
    }
    while(firstStrikeToBeConsidered <=lastStrikeToBeConsidered && (!Number.isFinite(originalStrikesArray[firstStrikeToBeConsidered].callOi) || Number(originalStrikesArray[firstStrikeToBeConsidered].callOi) < thresholdOi) && (!Number.isFinite(originalStrikesArray[firstStrikeToBeConsidered].putOi) || Number(originalStrikesArray[firstStrikeToBeConsidered].putOi) < thresholdOi)){
        firstStrikeToBeConsidered++;
    }
    let startIndex = originalStrikesArray.findIndex((dt) => dt.strike === originalStrikesArray[firstStrikeToBeConsidered].strike)
    let endIndex = originalStrikesArray.findIndex((dt) => dt.strike === originalStrikesArray[lastStrikeToBeConsidered].strike)
        
    if (startIndex > 1) {
        startIndex -= 2
    } else if (startIndex > 0) {
        startIndex -= 1
    }
    if (endIndex < originalStrikesArray.length - 2) {
        endIndex += 2
    } else if (endIndex < originalStrikesArray.length - 1) {
        endIndex += 1
    }
    const actualStartIndex = strikesArray.findIndex((a) => a === originalStrikesArray[startIndex].strike)
    const actualEndIndex = strikesArray.findIndex((a) => a === originalStrikesArray[endIndex].strike)
    return actualEndIndex !== -1 && actualStartIndex !== -1 && actualEndIndex + 1 > actualStartIndex ? array.slice(actualStartIndex, actualEndIndex + 1) : array
}


export const CalculateProfit = (data: OiDataTypeBars[], orders: OrdersDataType[], orderPrices: OrderDataPrices[], security: Security | undefined, setData: Function, targetDaysFromToday: number, referenceTimestamp: number, customSpotPrice?: number) => {
    try {
        const newData = [...data]
        const minExpiry = Math.min(...orders.map(order => {
            const numberOfDaysToExpiry = calculateDifferenceInDays(order.expiry, referenceTimestamp)
            return numberOfDaysToExpiry
        }))
        newData.forEach((d) => {
            let profit = 0, expectedProfit = 0
            const spotStrike = customSpotPrice ?? (d.strike ?? 0)
            orders.forEach((order, index) => {
                const premium = order.contractType === 'FUTURE' ? 0 : Number(orderPrices[index]?.price ?? 0)
                const numberOfDaysToExpiry = calculateDifferenceInDays(order.expiry, referenceTimestamp)
                const t = numberOfDaysToExpiry
                const orderStrike = order.contractType === 'FUTURE' ? Number(orderPrices[index]?.price ?? 0) : (order.data.strikeCol.strike / (security?.priceMultiplier ?? 100))
                let priceOnExpiry: number = 0
                if(order.contractType === 'FUTURE'){
                    priceOnExpiry = spotStrike - orderStrike
                }else if(t === minExpiry || order?.sqOffByOrderId || order?.sqOffWithOrderId){
                    priceOnExpiry = Math.max((order.callput === 'call'? 1 : -1) * (spotStrike - orderStrike), 0)
                }else{
                    priceOnExpiry = CalculateOptionPrice(spotStrike, order.data.strikeCol.strike / (security?.priceMultiplier ?? 100.0), 0.1, (t - minExpiry) / 365.0, order.callput === 'call' ? order.data.callOption!!.impliedVolatility : order.data.putOption!!.impliedVolatility, order.callput === 'call')
                }

                let priceOnTargetDate: number = 0
                if(order.contractType === 'FUTURE'){
                    priceOnTargetDate = spotStrike - orderStrike
                }else if(targetDaysFromToday >= t || order?.sqOffByOrderId || order?.sqOffWithOrderId){
                    priceOnTargetDate = Math.max((order.callput === 'call' ? 1 : -1) * (spotStrike - orderStrike), 0)
                }else{
                    priceOnTargetDate = CalculateOptionPrice(spotStrike, order.data.strikeCol.strike / (security?.priceMultiplier ?? 100.0), 0.1, (t - targetDaysFromToday) / 365.0, order.callput === 'call' ? order.data.callOption!!.impliedVolatility : order.data.putOption!!.impliedVolatility, order.callput === 'call')
                }

                profit += (priceOnExpiry - premium) * (order.type === 'buy' ? 1 : -1) * (order.quantity) * (order.security?.lotSize ?? 50)
                expectedProfit += (order.type === 'buy' ? 1 : -1) * (priceOnTargetDate - premium) * (order.quantity) * (order.security?.lotSize ?? 50)
            })
            d.profit = Number(profit.toFixed(2))
            d.expectedProfit = Number(expectedProfit.toFixed(2))
        })
        setData(newData)
        return newData
    } catch (e) {
        return data
    }
}

export const CalculateBasketAnalyzeProfit = (data: PayoffDataType[], orders: OrdersDataType[], orderPrices: OrderDataPrices[], security: Security | undefined, setData: Function, targetDaysFromToday: number, customSpotPrice?: number) => {
    try {
        const newData = [...data]
        const minExpiry = Math.min(...orders.map(order => {
            const numberOfDaysToExpiry = order.contractType === 'FUTURE' ? order.data.numberOfDaysToExpiry!! : order.data!!.strikeCol!!.numberOfDaysToExpiry!!
            return Number(numberOfDaysToExpiry!!.substring(0, numberOfDaysToExpiry!!.length - 1))
        }))
        newData.forEach((d) => {
            let profit = 0, expectedProfit = 0
            const spotStrike = customSpotPrice ?? (d.strike ?? 0)
            orders.forEach((order, index) => {
                const premium = order.contractType === 'FUTURE' ? 0 : Number(orderPrices[index]?.price ?? 0)
                const numberOfDaysToExpiry = order.contractType === 'FUTURE' ? order.data.numberOfDaysToExpiry!! : order.data!!.strikeCol!!.numberOfDaysToExpiry!!  
                let t = Number(numberOfDaysToExpiry!!.substring(0, numberOfDaysToExpiry!!.length - 1))
                const orderStrike = (order.contractType === 'FUTURE' ? Number(orderPrices[index]?.price ?? 0) : order.data.strikeCol.strike) / (security?.priceMultiplier ?? 100)
                
                let priceOnExpiry: number = 0
                if(order.contractType === 'FUTURE'){
                    priceOnExpiry = spotStrike - orderStrike
                }else if(t === minExpiry){
                    priceOnExpiry = Math.max((order.callput === 'call'? 1 : -1) * (spotStrike - orderStrike), 0)
                }else{
                    priceOnExpiry = CalculateOptionPrice(spotStrike, order.data.strikeCol.strike / (security?.priceMultiplier ?? 100.0), 0.1, (t - minExpiry) / 365.0, order.callput === 'call' ? order.data.callOption!!.impliedVolatility : order.data.putOption!!.impliedVolatility, order.callput === 'call')
                }

                let priceOnTargetDate: number = 0
                if(order.contractType === 'FUTURE'){
                    priceOnTargetDate = spotStrike - orderStrike
                }else if(targetDaysFromToday >= t){
                    priceOnTargetDate = Math.max((order.callput === 'call' ? 1 : -1) * (spotStrike - orderStrike), 0)
                }else{
                    priceOnTargetDate = CalculateOptionPrice(spotStrike, order.data.strikeCol.strike / (security?.priceMultiplier ?? 100.0), 0.1, (t - targetDaysFromToday) / 365.0, order.callput === 'call' ? order.data.callOption!!.impliedVolatility : order.data.putOption!!.impliedVolatility, order.callput === 'call')
                }

                profit += (priceOnExpiry - premium) * (order.type === 'buy' ? 1 : -1) * (order.quantity) * (order.security?.lotSize ?? 50)
                expectedProfit += (order.type === 'buy' ? 1 : -1) * (priceOnTargetDate - premium) * (order.quantity) * (order.security?.lotSize ?? 50)
            })
            d.basketAnalyzeProfit = Number(profit.toFixed(2))
            d.basketAnalyzeExpectedProfit = Number(expectedProfit.toFixed(2))
        })
        setData(newData)
        return newData
    } catch (e) {
        return data
    }
}

export function addDaysToDate(daysToAdd: number, referenceTime?: number) {
    const today = referenceTime ? new Date(referenceTime) : new Date()
    today.setDate(today.getDate() + daysToAdd)
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
    return today.toLocaleDateString('en-US', options)
}

export const addDaysToTimestamp = (timestamp: number, days: number): number => {
    return timestamp + days*(24*60*60000);
}

export function findNeighbor(arr: number[], target: number, direction: 'left' | 'right') {
    let left = 0;
    let right = arr.length - 1;
    let result = target;

    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (arr[mid] < target) {
            if (direction === 'left') result = arr[mid];
            left = mid + 1;
        } else if (arr[mid] > target) {
            if (direction === 'right') result = arr[mid];
            right = mid - 1;
        } else {
            // Exact match found
            if (direction === 'left') result = arr[mid - 1] ?? target;
            if (direction === 'right') result = arr[mid + 1] ?? target;
            return result;
        }
    }

    return result;
}

export function updateIvFromAtmIv(orders: OrdersDataType[], optionsTradesData: optionsTradesDataType[], ivMultiplier: number): OrdersDataType[] {
    return orders.map((order: OrdersDataType) => {
        if(order.contractType === 'FUTURE') return order
        const optionExpiry = optionsTradesData.find((o) => o.expiration.expiry?.expiry === order.expiry)
        if (!optionExpiry) return order
        // const atmstrike = optionExpiry.atmStrike
        // const atmOptions = optionExpiry.trades.find((trade) => trade.strikeCol.strike === atmstrike)!
        // if (!atmOptions) return order
        const callIv = (order.data.callOption?.impliedVolatility ?? 0) * ivMultiplier
        const putIv = (order.data.putOption?.impliedVolatility ?? 0) * ivMultiplier

        return {
            ...order,
            data: {
                ...order.data,
                callOption: {
                    ...order.data.callOption,
                    impliedVolatility: callIv
                },
                putOption: {
                    ...order.data.putOption,
                    impliedVolatility: putIv
                }
            }
        } as OrdersDataType
    })
}

function sortByType(a: TradeOrder, b: TradeOrder) {
    if (a.quantity > 0 && b.quantity < 0) {
        return -1
    } else if (a.quantity < 0 && b.quantity > 0) {
        return 1
    } else {
        return 0
    }
}

export const sortOrderByBuySell = (orders: TradeOrder[]) => {
    const newOrders = [...orders]
    return newOrders.sort(sortByType)
}

function sortPositionByType(a: GroupedPosition, b: GroupedPosition) {
    if (a.quantity > 0 && b.quantity <0) {
        return -1
    } else if (a.quantity < 0 && b.quantity > 0) {
        return 1
    } else {
        return 0
    }
}

export const sortPositionsByBuySell = (positions: GroupedPosition[]) => {
    const newOrders = [...positions]
    return newOrders.sort(sortPositionByType)
}

export const isOrderFieldEditable = (fieldName: 'buySell' | 'expiry' | 'strike' | 'callPut' | 'quantity' | 'price' | 'open' | 'pending' | 'filled' | 'rejected', orderStatus: OrderStatus, orderState: OrderState): boolean => {
    switch (fieldName) {
        case 'buySell': {
            return !([OrderStatus.PENDING, OrderStatus.OPEN, OrderStatus.PARTIALLY_FILLED, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus) || (orderState === 'square-off') || (orderState === 'waiting-square-off'))
        }
        case 'expiry': {
            return !([OrderStatus.PENDING, OrderStatus.OPEN, OrderStatus.PARTIALLY_FILLED, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus) || (orderState === 'square-off') || (orderState === 'waiting-square-off'))
        }
        case 'strike': {
            return !([OrderStatus.PENDING, OrderStatus.OPEN, OrderStatus.PARTIALLY_FILLED, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus) || (orderState === 'square-off') || (orderState === 'waiting-square-off'))
        }
        case 'callPut': {
            return !([OrderStatus.PENDING, OrderStatus.OPEN, OrderStatus.INACTIVE, OrderStatus.TRIGGER_PENDING, OrderStatus.PARTIALLY_FILLED, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus) || (orderState === 'square-off') || (orderState === 'waiting-square-off'))
        }
        case 'quantity': {
            return ![OrderStatus.PENDING, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus)
        }
        case 'price': {
            return ![OrderStatus.PENDING, OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderStatus)
        }
        case 'open': {
            return [OrderStatus.OPEN, OrderStatus.PARTIALLY_FILLED].includes(orderStatus)
        }
        case 'pending': {
            return [OrderStatus.PENDING].includes(orderStatus)
        }
        case 'filled': {
            return [OrderStatus.EXECUTED].includes(orderStatus)
        }
        case 'rejected': {
            return [OrderStatus.REJECTED, OrderStatus.FAILED].includes(orderStatus)
        }
    }
}

export const checkForDuplicateOrders = (orders: OrdersDataType[]): boolean => {
    const map: {
        [key: string]: number
    } = {}
    orders.filter(o=>!o?.readonly).forEach((order) => {
        // const key = `${order.security?.code}-${order.type}-${order.contractType === 'OPTION' ? `${order.data.strikeCol.strike}-${order.callput}` : ''}`
        const key = `${order.security?.code}-${order.contractType === 'OPTION' ? `${order.data.strikeCol.strike}-${order.callput}` : ''}`
        map[key] = (map[key] || 0) + 1
    })
    return Object.values(map).some(count => count > 1)
}


export const checkForDuplicateTradeOrders = (orders: TradeOrder[]): boolean => {
    const securitySet = new Set<string>()

    for (let order of orders) {
        const security = order.security;
        if (!security) continue;
        const key = `${security?.code}-${security.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? `${security.strikePrice}-${security.optionType}` : ''}`

        if (securitySet.has(key)) {
            return true;
        } else {
            securitySet.add(key)
        }
    }
}

export const getOrderBooksForOrders = async (orders: OrdersDataType[]): Promise<OrderBook[]> => {
    const orderBooks = store.getState().securityStore.orderBooks
    const securitiesToFetchFor = orders.filter((order) => !orderBooks[getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')]).map(o=>o.security as Security)
    const orderBooksData = await getOrderBooksBulk(securitiesToFetchFor)
    if(orderBooksData.length) store.dispatch(updateOrderBooks(orderBooksData))
    let fetchedOrderBooksData: Record<string, OrderBook> = {}
    orderBooksData.forEach((orderBook) => {
        const {security} = orderBook
        if (!security) return;
        const key = getSecurityKey(security.exchange, security.code)
        fetchedOrderBooksData = {
            ...fetchedOrderBooksData,
            [key]: orderBook
        }  
    })

    return Promise.resolve(orders.map((order) => {
        const securityKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
        return (orderBooks[securityKey] ?? fetchedOrderBooksData[securityKey] ) as OrderBook
    }))
}

export const setCurentOrderPrices = async(orders: OrdersDataType[], orderPrices: OrderDataPrices[], dispatch: Dispatch, completeReset: boolean = true, resetPriceIndex: number = 0) => {
    if(completeReset) {
        const securities = orders.filter(o=>o.visibility).map(o=>({
            code: o.security.code,
            exchange: o.security.exchange
        } as Security))
        const orderBooksData = await getOrderBooksBulk(securities)
        const newOrdersPrices: OrderDataPrices[] = []
        for (let i = 0; i < orders.length; i++) {
            const order = orders[i]
            if(order.visibility) {
                if (!(order.orderDetails?.status === OrderStatus.EXECUTED)) {
                    const priceMultiplier = order.security?.priceMultiplier ?? 100
                    const data = orderBooksData.find(o => o.security?.exchange === order.security?.exchange && o.security?.code === order.security?.code)
                    if (data) {
                        let topAsk = Number(((data.asks[0]?.price ?? 0) / priceMultiplier).toFixed(2))
                        let topBid = Number(((data.bids[0]?.price ?? 0) / priceMultiplier).toFixed(2))
                        if (topAsk <= 0 && topBid <= 0) {
                            const key = getSecurityKey(order.security.exchange, order.security.code)
                            const raw = limitPriceFromOrderBookOrLtp(
                                data,
                                orderTypeToTransactionType(order.type),
                                store.getState().securityStore.snapshots[key]?.lastTradedPrice
                            )
                            if (raw > 0) {
                                const dec = Number((raw / priceMultiplier).toFixed(2))
                                topAsk = dec
                                topBid = dec
                            }
                        }
                        let mid = Number(((topAsk + topBid) / 2).toFixed(2))
                        mid = roundToNearestMultiple(mid, orderTypeToTransactionType(order.type))
                        newOrdersPrices.push({
                            price: mid.toFixed(2),
                            topAsk,
                            topBid,
                            mid,
                            visibility: order.visibility
                        })
                    } else {
                        newOrdersPrices.push(orderPrices[i])
                    }
                } else {
                    newOrdersPrices.push(orderPrices[i])
                }
            } else {
                newOrdersPrices.push(orderPrices[i])
            }
        }
        dispatch(setOrderPrices(newOrdersPrices))
        return newOrdersPrices
    } else {
        const newOrdersPrices: OrderDataPrices[] = [...orderPrices]
        const order = orders[resetPriceIndex]
        if (!(order.orderDetails?.status === OrderStatus.EXECUTED)) {
            const priceMultiplier = order.security?.priceMultiplier ?? 100
            const data = await getOnlyOrderBook({ security: order.security })
            let topAsk = Number(((data.asks[0]?.price ?? 0) / priceMultiplier).toFixed(2))
            let topBid = Number(((data.bids[0]?.price ?? 0) / priceMultiplier).toFixed(2))
            if (topAsk <= 0 && topBid <= 0) {
                const key = getSecurityKey(order.security.exchange, order.security.code)
                const raw = limitPriceFromOrderBookOrLtp(
                    data,
                    orderTypeToTransactionType(order.type),
                    store.getState().securityStore.snapshots[key]?.lastTradedPrice
                )
                if (raw > 0) {
                    const dec = Number((raw / priceMultiplier).toFixed(2))
                    topAsk = dec
                    topBid = dec
                }
            }
            let mid = Number(((topAsk + topBid) / 2).toFixed(2))
            mid = roundToNearestMultiple(mid, orderTypeToTransactionType(order.type))
            newOrdersPrices[resetPriceIndex] = {
                price: mid.toFixed(2),
                topAsk,
                topBid,
                mid,
                visibility: order.visibility
            }
        }
        dispatch(setOrderPrices(newOrdersPrices))
        return newOrdersPrices
    }
}

export const getCurentOrderPrices = async (orders: OrdersDataType[], completeReset: boolean = true, resetPriceIndex: number = 0, orderPrices: OrderDataPrices[] = []) => {
    if (completeReset) {
        const newOrdersPrices: OrderDataPrices[] = []
        for (let i = 0; i < orders.length; i++) {
            const order = orders[i]
            if(!(order.orderDetails?.status===OrderStatus.EXECUTED)) {
                const data = await getOnlyOrderBook({security: order.security})
                newOrdersPrices.push(await getCurrentOrderPriceFromData(order, [], [], data))
            }
        }
        return newOrdersPrices
    } else {
        const newOrdersPrices: OrderDataPrices[] = [...orderPrices]
        const order = orders[resetPriceIndex]
        if(!(order.orderDetails?.status===OrderStatus.EXECUTED)) {
            const data = await getOnlyOrderBook({security: order.security})
            newOrdersPrices[resetPriceIndex] = await getCurrentOrderPriceFromData(order, [], [], data)
        }
        return newOrdersPrices
    }
}


export const getCurrentOrderPriceFromData = async (order: OrdersDataType, optionExpiries: optionsTradesDataType[], futureExpiries: futureTradeDataType[], orderBook: OrderBook | undefined): Promise<OrderDataPrices> => {
    const priceMultiplier = order.security?.priceMultiplier??100
    const optionExpiryIndex = optionExpiries.findIndex(o=>Number(o.expirySeconds)===order.expiry)
    const futureExpiryIndex = futureExpiries.findIndex(o=>Number(o.expiry)===order.expiry)
    let orderPrice: OrderDataPrices = {
        price: '0',
        topAsk: 0,
        topBid: 0,
        mid: 0,
        visibility: order.visibility
    }
    let topAsk: number = Number(((orderBook?.asks?.[0]?.price??0)/priceMultiplier).toFixed(2)), topBid: number = Number(((orderBook?.bids?.[0]?.price??0)/priceMultiplier).toFixed(2))

    if(optionExpiryIndex>=0 && order.contractType === 'OPTION') {
        const strike = optionExpiries[optionExpiryIndex].trades.find(s=>s.strikeCol.strike===order.security?.strikePrice)
        if(strike) {
            const option = store.getState().securityStore.candleChangeBook[getSecurityKey(order.security.exchange, order.security.code)]
            const strikeOption = order.callput === 'call' ? strike.callOption : strike.putOption
            topAsk = Number(((option?.topAsk??strikeOption.topAsk)/(priceMultiplier)).toFixed(2))
            topBid = Number(((option?.topBid??strikeOption.topBid)/(priceMultiplier)).toFixed(2))
        }
    }else if (futureExpiryIndex >= 0 && order.contractType === 'FUTURE'){
        const future = futureExpiries[futureExpiryIndex]
        topAsk = Number(((future?.ask??0)/(priceMultiplier)).toFixed(2))
        topBid = Number(((future.bid??0)/(priceMultiplier)).toFixed(2))
    }else if (orderBook === undefined){
        const ob = await getOnlyOrderBook({security: order.security})
        topAsk = Number(((ob?.asks?.[0]?.price??0)/priceMultiplier).toFixed(2))
        topBid = Number(((ob?.bids?.[0]?.price??0)/priceMultiplier).toFixed(2))
    }
    const snapKey = getSecurityKey(order.security.exchange, order.security.code)
    const snapLtp = store.getState().securityStore.snapshots[snapKey]?.lastTradedPrice
    if (topAsk <= 0 && topBid <= 0) {
        const ob = orderBook ?? store.getState().securityStore.orderBooks[snapKey]
        const raw = limitPriceFromOrderBookOrLtp(ob, orderTypeToTransactionType(order.type), snapLtp)
        if (raw > 0) {
            const dec = Number((raw / priceMultiplier).toFixed(2))
            topAsk = dec
            topBid = dec
        }
    }
    if(Number.isFinite(topAsk) && Number.isFinite(topBid)){
        let mid = Number(((topAsk + topBid) / 2).toFixed(2))
        mid = roundToNearestMultiple(mid, orderTypeToTransactionType(order.type))
        orderPrice = {
            price: mid.toFixed(2),
            topAsk,
            topBid,
            mid,
            visibility: order.visibility
        }
    }
    return orderPrice
}

export const getCurrentOrderLtpPriceFromData = (order: OrdersDataType, optionExpiries: optionsTradesDataType[], futureExpiries: futureTradeDataType[]): OrderDataPrices => {
    const priceMultiplier = order.security?.priceMultiplier??100
    const optionExpiryIndex = optionExpiries.findIndex(o=>Number(o.expirySeconds)===order.expiry)
    const futureExpiryIndex = futureExpiries.findIndex(o=>Number(o.expiry)===order.expiry)
    let orderPrice: OrderDataPrices = {
        price: '0',
        topAsk: 0,
        topBid: 0,
        mid: 0,
        visibility: order.visibility
    }
    let topAsk = 0, topBid = 0

    if(optionExpiryIndex>=0 && order.contractType === 'OPTION') {
        const strike = optionExpiries[optionExpiryIndex].trades.find(s=>s.strikeCol.strike===order.security?.strikePrice)
        if(strike) {
            const option = order.callput === 'call' ? strike.callOption : strike.putOption
            topAsk = Number(((option?.ltp??0)/(priceMultiplier)).toFixed(2))
            topBid = Number(((option?.ltp??0)/(priceMultiplier)).toFixed(2))
        }
    }else if (futureExpiryIndex >= 0 && order.contractType === 'FUTURE'){
        const future = futureExpiries[futureExpiryIndex]
        topAsk = Number(((future?.ltp??0)/(priceMultiplier)).toFixed(2))
        topBid = Number(((future.ltp??0)/(priceMultiplier)).toFixed(2))
    }
    if(Number.isFinite(topAsk) && Number.isFinite(topBid)){
        let mid = topAsk
        orderPrice = {
            price: mid.toFixed(2),
            topAsk,
            topBid,
            mid,
            visibility: order.visibility
        }
    }
    return orderPrice
}

export const getCurentOrderPricesFromData = async (orders: OrdersDataType[], optionExpiries: optionsTradesDataType[], underlyingKey: string, orderBooks: (OrderBook | undefined)[], completeReset: boolean = true, resetPriceIndex: number = 0, orderPrices: OrderDataPrices[] = []): Promise<OrderDataPrices[]> => {
    const futureExpiries = store.getState().futuresStore.futuresData[underlyingKey] || []
    if(completeReset) {
        const newOrdersPrices: OrderDataPrices[] = []
        for(let i = 0; i<orders.length; i++) {
            const order = orders[i]
            const orderPrice = await getCurrentOrderPriceFromData(order, optionExpiries, futureExpiries, orderBooks[i])
            newOrdersPrices.push(orderPrice)
        }
        return newOrdersPrices
    } else {
        const newOrdersPrices: OrderDataPrices[] = [...orderPrices]
        const order = orders[resetPriceIndex]
        if(!(order.orderDetails?.status===OrderStatus.EXECUTED)) {
            newOrdersPrices[resetPriceIndex] = await getCurrentOrderPriceFromData(order, optionExpiries, futureExpiries,orderBooks[resetPriceIndex])
        }
        return newOrdersPrices
    }
}

export const getCurentOrderLtpPricesFromData = (orders: OrdersDataType[], optionExpiries: optionsTradesDataType[], underlyingKey: string, orderPrices: OrderDataPrices[] = []): OrderDataPrices[] => {
    const futureExpiries = store.getState().futuresStore.futuresData[underlyingKey] || []
    const newOrdersPrices: OrderDataPrices[] = []
    for(let i = 0; i<orders.length; i++) {
        const order = orders[i]
        const orderPrice = (order?.traded && !!orderPrices[i]) ? orderPrices[i] : getCurrentOrderLtpPriceFromData(order, optionExpiries, futureExpiries)
        newOrdersPrices.push({
            ...orderPrice,
            traded: order?.traded??false
        })
    }
    return newOrdersPrices
}

export const setCurentOrderPricesFromData = async (orders: OrdersDataType[], dispatch: Dispatch, optionExpiries: optionsTradesDataType[], underlyingKey: string,orderBooks: (OrderBook | undefined)[], completeReset: boolean = true, resetPriceIndex: number = 0, orderPrices: OrderDataPrices[] = []) => {
    const futureExpiries = store.getState().futuresStore.futuresData[underlyingKey] || []
    if(completeReset) {
        const newOrdersPrices: OrderDataPrices[] = []
        for(let i = 0; i<orders.length; i++) {
            const order = orders[i]
            const orderPrice = await getCurrentOrderPriceFromData(order, optionExpiries, futureExpiries, orderBooks[i])
            newOrdersPrices.push(orderPrice)
        }
        dispatch(setOrderPrices(newOrdersPrices))
    } else {
        const newOrdersPrices: OrderDataPrices[] = [...orderPrices]
        const order = orders[resetPriceIndex]
        if(!(order.orderDetails?.status===OrderStatus.EXECUTED)) {
            newOrdersPrices[resetPriceIndex] = await getCurrentOrderPriceFromData(order, optionExpiries, futureExpiries,orderBooks[resetPriceIndex])
        }
        dispatch(setOrderPrices(newOrdersPrices))
    }
}

export const updateDataWithBreakevenPoints = (data: OiDataTypeBars[]) => {
    const newData: OiDataTypeBars[] = []
    let lastDataPoint: OiDataTypeBars | undefined = undefined
    const breakeven: number[] = []
    const zeroValueStrikes: number[] = []
    data.forEach((dt, ind) => {
        if (dt.profit !== 0 && !(lastDataPoint && ((lastDataPoint.profit < 0 && dt.profit > 0) || (lastDataPoint.profit > 0 && dt.profit < 0)))) {
            newData.push(dt)
            lastDataPoint = dt
            return
        }
        if (dt.profit === 0 && dt.strike !== undefined) {
            zeroValueStrikes.push(dt.strike)
        } else if (lastDataPoint && lastDataPoint.strike !== undefined && dt.strike !== undefined) {
            const newStrikePoint = lastDataPoint.strike + (((0 - lastDataPoint.profit) * (dt.strike - lastDataPoint.strike)) / (dt.profit - lastDataPoint.profit))
            newData.push({
                strike: newStrikePoint,
                profit: 0,
                expectedProfit: NaN,
                duplicateStrike: false,
                callOi: 0,
                putOi: 0,
                callDelta: 0,
                putDelta: 0,
            })
            if (Number.isFinite(newStrikePoint)) {
                breakeven.push(newStrikePoint)
            }
        }
        newData.push(dt)
        lastDataPoint = dt
    })

    if (zeroValueStrikes.length) {
        breakeven.push(zeroValueStrikes[0])
        if (zeroValueStrikes.length > 1) {
            breakeven.push(zeroValueStrikes[zeroValueStrikes.length - 1])
        }
    }
    return {
        updatedData: newData,
        breakeven: breakeven.sort()
    }
}

type Trade = {quantity: number, security: Security};
export const getStrategyExpressionParams = (trades: Array<Trade>) => {
    const gcd = calculateGcd(trades.filter(o => o.quantity !== 0).map((o) => Math.abs(o.quantity)))

    const sorted = sortTradeOrdersByTransactionType(trades);
    let param = '';
    const securityKeys: string[] = [];
    sorted.forEach((order) => {
        const num = Math.abs(order.quantity / gcd);
        const sKey = getSecurityKey(order.security!!.exchange, order.security!!.code)
        securityKeys.push(sKey)
        param += `${order.quantity > 0 ? '+' : '-'}${num > 1 ? num + '*' : ''}${sKey}`
    })
    if (param[0] === '+') {
        param = param.slice(1)
    }

    const err = isExpressionValid(param)
    let securities: string[] = []
    if (!err) {
        securities = Array.from(new Set(securityKeys))
    }
    return { param, securities};
}

export const sortTradeOrdersByTransactionType = (trades: Trade[]) => {
    const sorted = [...trades];

    return sorted.sort((a, b) => {
        if (a.quantity > 0 && b.quantity < 0) {
            return -1
        } else if (a.quantity < 0 && b.quantity > 0) {
            return 1
        } else {
            return 0
        }
    })
}

export const getStrategyInfo = async (tradeOrders: TradeOrder[], positions: Position[], optionsTradesData: optionsTradesDataType[]): Promise<Partial<StrategyInfo> | void> => {
    let strategyInfo: Partial<StrategyInfo> = {}
    const legs = [...tradeOrders].filter(to => to.selected);
    const tradeExpiries = await getSortedTradedExpiries(tradeOrders, positions);

    if (!tradeExpiries?.length) return;
    const minExpiry = tradeExpiries[0];
    const optionsTradesDataMap: Record<number, optionsTradesDataType> = optionsTradesData.reduce((acc, o) => ({...acc, [o.expiration.expiry.expiry]: o}), {} as Record<number, optionsTradesDataType> )

    const nearestExpiryData = optionsTradesDataMap[minExpiry];

    if (!nearestExpiryData || nearestExpiryData.strikes.length < 2) return;

    let realizedProfit = positions.reduce((sum, p) => sum + p.realisedProfit, 0);
    let tradePoints = legs.map((t) => t.security.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? t.security.strikePrice : null).filter(v => !!v);
    const priceMultiplier = legs[0]?.security.priceMultiplier??100;
    for (let i = 0; i < (positions?.length ?? 0); i++) {
        const p = positions![i]
        const {security, closePosition, closePositionValue} = p;
        const sec = await getSecurityAsync(getSecurityKey(security.exchange, security.code));
        if (!closePosition || !sec) continue;
        legs.push({
            security: sec,
            clientOrderId: i.toString(),
            quantity: Math.abs(closePosition),
            selected: true,
            transactionType: closePosition > 0 ? TransactionType.BUY : TransactionType.SELL,
            price: closePositionValue/closePosition
        })
        if (sec.categoryCode === SecurityCategory_CategoryCode.OPTIONS &&
            !tradePoints.includes(sec.strikePrice)
        ) {
            tradePoints.push(sec.strikePrice)
        }
    }
    tradePoints = tradePoints.sort();
    if (!tradePoints.length ) return;

    strategyInfo.totalPremium = legs.reduce((sum, leg) => {
        if (leg.security.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
            if (leg.transactionType === TransactionType.BUY) {
                sum += leg.quantity * (leg.price ?? 0);
            } else {
                sum -= leg.quantity * (leg.price ?? 0);
            }
        }
        return sum;
    }, 0);
    let profits = tradePoints.map(evaluationPrice => {
        return getExpiryProfitForLegsAtPrice(legs, evaluationPrice, realizedProfit, minExpiry, minExpiry, optionsTradesDataMap)
    })
    const zeroPriceProfit = getExpiryProfitForLegsAtPrice(legs, 0, realizedProfit, minExpiry, minExpiry, optionsTradesDataMap);
    let pop = zeroPriceProfit > 0 ? 1 : 0;
    const endPrice = tradePoints[tradePoints.length - 1]*2;
    const endPriceProfit = getExpiryProfitForLegsAtPrice(legs, endPrice, realizedProfit, minExpiry, minExpiry, optionsTradesDataMap);
    let maxLoss = Math.min(...profits);
    let maxProfit = Math.max(...profits);

    if ( zeroPriceProfit > profits[0] || endPriceProfit > profits[profits.length - 1] ) {
        maxProfit = Number.POSITIVE_INFINITY;
    }

    if ( zeroPriceProfit < profits[0] || endPriceProfit < profits[profits.length - 1] ) {
        maxLoss = Number.NEGATIVE_INFINITY;
    }

    strategyInfo.maxLoss = maxLoss;
    strategyInfo.maxProfit = maxProfit;

    let previousProfit = profits[0];
    let previousStrike = tradePoints[0];
    let breakEvens: number[] = []

    if (zeroPriceProfit*previousProfit < 0) {
        breakEvens.push(findInflectionPoint(0, zeroPriceProfit, previousStrike, previousProfit)/priceMultiplier);
    }
    for (let i=1; i < tradePoints.length; i ++) {
        const currentProfit = profits[i];
        const currentStrike = tradePoints[i]
        if (currentProfit*previousProfit < 0) {
            breakEvens.push(findInflectionPoint(previousStrike, previousProfit, currentStrike, currentProfit)/priceMultiplier)
        }
        previousProfit = currentProfit;
        previousStrike = currentStrike
    }

    if (previousProfit*endPriceProfit < 0) {
        breakEvens.push(findInflectionPoint(previousStrike, previousProfit, endPrice, endPriceProfit)/priceMultiplier)
    }

    if (breakEvens.length) {
        strategyInfo.breakeven = breakEvens
    }

    // for pop calculation — Sensibull-style neighbor fill; "-" if any BE has no Δ below/above
    let breakEvenIndex = 0;
    let previousProfitDirection = Math.sign(zeroPriceProfit);
    let popResolvable = true;

    if (optionsTradesData) {
        const trades = nearestExpiryData.trades ?? []
        while (breakEvenIndex < breakEvens.length) {
            const breakeEenPoint = breakEvens[breakEvenIndex]
            const deltaAtBreakeven = resolveCallDeltaAtBreakeven(trades, breakeEenPoint * priceMultiplier)
            if (deltaAtBreakeven == null) {
                popResolvable = false
                break
            }
            if (previousProfitDirection < 0) {
                pop += deltaAtBreakeven
            } else {
                pop -= deltaAtBreakeven
            }
            breakEvenIndex += 1
            previousProfitDirection *= -1
        }

        // 0 → header shows "-" (same as prior empty/failed POP). Valid compute is clamped 0–100.
        strategyInfo.pop = popResolvable ? clampPopPercent(pop) : 0

        const optionDataMap = trades.reduce((acc, ot) => {
            if (ot.callOption) {
                const securityKey = getSecurityKey(ot.callOption.security.exchange, ot.callOption.security.code);
                acc[securityKey] = ot.callOption.optionGreeks;
            }
            if (ot.putOption) {
                const securityKey = getSecurityKey(ot.putOption.security.exchange, ot.putOption.security.code);
                acc[securityKey] = ot.putOption.optionGreeks;
            }

            return acc;
        }, {} as Record<string, OptionGreeks>) ?? {}

        const greeks = legs.reduce((acc, l) => {
            const direction = l.transactionType === TransactionType.BUY ? 1 : -1;
            const key = getSecurityKey(l.security.exchange, l.security.code);
            return {
                ...acc,
                delta: acc.delta + direction*l.quantity*(optionDataMap[key]?.delta??0),
                theta: acc.theta + direction*l.quantity*(optionDataMap[key]?.theta??0),
                gamma: acc.gamma + direction*l.quantity*(optionDataMap[key]?.gamma??0),
                vega: acc.vega + direction*l.quantity*(optionDataMap[key]?.vega??0)
            }
        }, {
            delta: 0,
            theta: 0,
            gamma: 0,
            vega: 0
        } as OptionGreeks)

        if (greeks) {
            strategyInfo = {
                ...strategyInfo,
                ...greeks
            }
        }
    }
    return strategyInfo
}

export const findInflectionPoint = (x1, y1, x2, y2) => {
    return (x1*Math.abs(y2) + x2*Math.abs(y1))/(Math.abs(y1) + Math.abs(y2))
}

export const getExpiryProfitForLegsAtPrice = (legs: TradeOrder[], evaluationPrice: number, realizedProfit = 0, minExpiry: number, targetTimestamp: number, optionsTradesDataMap: Record<number, optionsTradesDataType>) => {

    const priceMultiplier = legs[0]?.security.priceMultiplier??100;
    let profitAtExpiry = realizedProfit;
    legs.forEach(l => {
        const { security, quantity, price, transactionType } = l
        const direction = transactionType === TransactionType.BUY ? 1 : -1;
        if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES){
            profitAtExpiry += quantity*(evaluationPrice - price)*direction
        } else {

            const timeToExpiry = calculateDifferenceInDays(security.expiry?.expiry, targetTimestamp)
            const timeToNearestExpiry = calculateDifferenceInDays(minExpiry, targetTimestamp);

            let orderPrice = l.price;
            const tradeData = optionsTradesDataMap[security.expiry.expiry]?.trades?.find(t => t.strikeCol.strike === security.strikePrice);
            const [, expiryProfit = 0] = getProfitsForSecurityAtDate(security, orderPrice, quantity*direction, timeToExpiry, timeToNearestExpiry, 1, evaluationPrice, evaluationPrice, tradeData)
            profitAtExpiry += expiryProfit;
        }
    })

    return profitAtExpiry/priceMultiplier;
}

export const getStrategyGreeks = async (tradeOrders: TradeOrder[], positions: Position[], optionsTradesData: optionsTradesDataType) => {
    if (!optionsTradesData) return;
    const optionDataMap = optionsTradesData.trades?.reduce((acc, ot) => {
        
        if (ot.callOption) {
            const securityKey = getSecurityKey(ot.callOption.security.exchange, ot.callOption.security.code);
            acc[securityKey] = ot.callOption.optionGreeks;
        }
        if (ot.putOption) {
            const securityKey = getSecurityKey(ot.putOption.security.exchange, ot.putOption.security.code);
            acc[securityKey] = ot.putOption.optionGreeks;
        }

        return acc;
    }, {} as Record<string, OptionGreeks>) ?? {}

    const legs = [...tradeOrders].filter(to => to.selected);

    for (let i = 0; i < (positions?.length ?? 0); i++) {
        const p = positions![i]
        const {security, closePosition, closePositionValue} = p;
        const sec = await getSecurityAsync(getSecurityKey(security.exchange, security.code));
        if (!closePosition || !sec) continue;
        legs.push({
            security: sec,
            clientOrderId: i.toString(),
            quantity: closePosition,
            selected: true,
            transactionType: closePosition > 0 ? TransactionType.BUY : TransactionType.SELL,
            price: closePositionValue/closePosition
        })
    }

    return legs.reduce((acc, l) => {
        const direction = l.transactionType === TransactionType.BUY ? 1 : -1;
        const key = getSecurityKey(l.security.exchange, l.security.code);
        return {
            ...acc,
            delta: acc.delta + direction*l.quantity*(optionDataMap[key]?.delta??0),
            theta: acc.theta + direction*l.quantity*(optionDataMap[key]?.theta??0),
            gamma: acc.gamma + direction*l.quantity*(optionDataMap[key]?.gamma??0),
            vega: acc.vega + direction*l.quantity*(optionDataMap[key]?.vega??0)
        }
    }, {
        delta: 0,
        theta: 0,
        gamma: 0,
        vega: 0
    } as OptionGreeks)
}


export const getExpiryDetailsForOrders = (optionsTradesData: optionsTradesDataType, calendarEvents: TradingCalendarData, referenceDate: Date): [number, number[]] => {
    const holidays = new Set(calendarEvents.holidays?.map((e) => e.date))
    const numberOfDaysString = optionsTradesData?.numberOfDaysToExpiry
    const numberOfDays = Number(numberOfDaysString?.substring(0, numberOfDaysString.length - 1))
    const daysToExpiryArray: number[] = []
    for (let i = 0; i <= numberOfDays; i++) {
        const current = new Date(referenceDate.getTime())
        current.setDate(current.getDate() + i)
        const yyyymmdd = [
            current.getFullYear(),
            String(current.getMonth() + 1).padStart(2, '0'),
            String(current.getDate()).padStart(2, '0')
        ].join('')
        const isWeekend = current.getDay() === 0 || current.getDay() === 6;
        if(!holidays.has(yyyymmdd) && !isWeekend) {
            daysToExpiryArray.push(i)
        }
    }
    return [numberOfDays, daysToExpiryArray]
}

export const isOrderBatchComplete = (tradeOrders: TradeOrder[], orderMap: Record<string, Order>): boolean => {
    return tradeOrders.every((order) => {
        return order.referenceOrderId && [OrderStatus.EXECUTED, OrderStatus.CANCELLED].includes(orderMap[order.referenceOrderId]?.status)
    })
}